import React, { useEffect } from "react";

/**
 * A hook that returns the current User as supplied by the Data Layer. As this requires the
 * Data Layer, this is a client-side only hook and will simply return undefined if run on the
 * server (as global.window will be undefined). As this is client-side, it also requires
 * any component using it to by hydrated in the browser for this to run.
 *
 * Components may only use the User object for personalization, and on Very Ireland the user must
 * have consented to targetting cookies for this to happen. Therefore the hook will only return
 * the user object if consent has been given.
 *
 * @returns The current User of the request, or undefined if not found, no consent given, or on server.
 */
const useUser = (): User | undefined => {
  const [user, setUser] = React.useState<User | undefined>(undefined);

  useEffect(() => {
    if (!window?.getDataLayer) return;
    const dataLayer = window.getDataLayer();

    if (dataLayer.brand !== "veryireland") {
      setUser(dataLayer.user);
      return;
    }

    // For Very Ireland we must check for consent, so we attach to an event handler
    // that will be fired by OneTrust when the page loads or when the user
    // accepts cookies in the modal.
    const OneTrustHandler = ((event: CustomEvent) => {
      // C0003 is the Functional Cookies consent, used for personalization.
      if (event?.detail?.includes("C0003")) {
        setUser(dataLayer.user);
      }
    }) as EventListener;

    window?.addEventListener("OneTrustGroupsUpdated", OneTrustHandler);

    return () => window?.removeEventListener("OneTrustGroupsUpdated", OneTrustHandler);
  }, []);

  return user as User | undefined;
};

export default useUser;
